<template>
  <div :class="{ 'no-results': objects === null || objects.length === 0, 'results-card': objects && objects.length > 0 }">
    <span v-if="objects === null">
      {{ $t('GlobalSearchResultsInstruction') }}
    </span>

    <span v-else-if="objects.length === 0">
      {{ $t('NoResults') }}
    </span>

    <div v-else-if="objects.length > 0" class="table-container">
      <span class="table-title">{{ $tc('Result', 2) }}</span>
      <Table
        :style="{'width': '100%'}"
        :loading="loading"
        :headers="headers"
        :items="objects"
        :emitRowClickBusEvent="true"
        :sortable="true"
        :scroll-id="scrollId"
        @onScrollBottom="onScrollBottom"
        @onScrollTop="onScrollTop"
        @sortClick="onSortClick"
      />
    </div>

    <loading-spinner
      :active.sync="loading"
      :is-full-page="true"
      color="var(--app-primary-base)"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { bus } from '@/main.js';
import Table from "@/newComponents/_Molecules/Table.vue";
import LoadingSpinner from "vue-loading-overlay";

export default {
  name: "GlobalSearchResults",

  components: {
    Table,
    LoadingSpinner,
  },

  data() {
    return {
      objects: null,
      layer: null,
      attribute: null,
      value: null,
      headers: [
        { text: this.$i18n.tc("Type", 1), varName: "type" },
        { text: "ID", varName: "id" },
        { text: this.$i18n.tc("Label", 1), varName: "label" },
      ],

      pageNumber: 1,
      pagination: 10,
      currentPageTop: 1,
      currentPageBot: 1,
      lastPage: 2,
      sortOrder: "id",
      loading: false,
    }
  },

  mounted() {
    bus.$on('execute-global-search', globalSearchData => {
      this.layer = globalSearchData.layer;
      this.attribute = globalSearchData.attribute;
      this.value = globalSearchData.value;
      this.initialSearch()
    })
  },

  destroyed() {
    bus.$off('execute-global-search')
  },

  computed: {
    selectedLayerPrefix() {
      if (this.layer) {
        return this.$store.getters.getLayerInitials(this.layer) + '_';
      }
      return null;
    },

    sortAttributeWithPrefix() {
      if (this.sortOrder && this.selectedLayerPrefix) {
        return this.sortOrder.charAt(0) === '-'
          ? this.sortOrder.slice(0, 1) + this.selectedLayerPrefix + this.sortOrder.slice(1)
          : this.selectedLayerPrefix + this.sortOrder;
      }
      return null;
    },

    scrollId() {
      return 'scroll-bar-results';
    },
  },

  methods: {
    ...mapActions([
      "SEARCH_OBJECTS_BY_ATTRIBUTE",
    ]),

    async initialSearch() {
      this.pageNumber = 1;
      this.currentPageBot = 1;
      this.currentPageTop = 1;
      this.loading = true;
      try {
        let queryParams = {
          paginate: this.pagination,
        }

        if (this.sortOrder !== null) {
          queryParams["sort"] = this.sortAttributeWithPrefix;
        }

        let typeObjectsFound = await this.SEARCH_OBJECTS_BY_ATTRIBUTE({
          layer: this.layer,
          filter: this.attribute,
          value: this.value,
          params: queryParams,
        });

        typeObjectsFound = this.removeObjectLayerPrefixFromAttributeKeys(
          typeObjectsFound.data.data[0].objects,
          typeObjectsFound.data.data[0].prefix
        );

        let itemsOptions = this.processingResultsData(typeObjectsFound)
        this.objects = [...itemsOptions]

      } catch (error) {
        console.error(error);
        this.handleError(error);
        this.objects = [];
      } finally {
        this.loading = false;
      }
    },

    onScrollBottom() {
      if (this.currentPageBot !== this.lastPage) {
        this.pageNumber++;
        this.getResultsBottom();
      }
    },

    async getResultsBottom() {
      this.loading = true;
      if (this.objects.length >= (3 * this.pagination)) {
        this.currentPageTop++;
      }
      this.currentPageBot++;

      try {
        let queryParams = {
          pageNumber: this.currentPageBot,
          paginate: this.pagination,
        };

        if (this.sortOrder !== null) {
          queryParams["sort"] = this.sortAttributeWithPrefix;
        }

        let resultsObject = await this.SEARCH_OBJECTS_BY_ATTRIBUTE({
          layer: this.layer,
          filter: this.attribute,
          value: this.value,
          params: queryParams,
        });

        this.lastPage = resultsObject.data.meta.last_page;
        resultsObject = this.removeObjectLayerPrefixFromAttributeKeys(
          resultsObject.data.data[0].objects,
          resultsObject.data.data[0].prefix
        );

        const allResults = [...this.objects, ...resultsObject];
        let itemsOptions = this.processingResultsData(allResults);

        this.objects = Array.from(new Set(itemsOptions.map(a => a.id))).map(id => { return itemsOptions.find(a => a.id === id) })
        if (this.objects.length > (3 * this.pagination)) {
          this.objects = this.objects.slice(this.objects.length - (3 * this.pagination));
        }

        this.scrollTableBottom();
      } catch (error) {
        console.error(error);
        this.handleError(error);
        this.objects = [];
      } finally {
        this.loading = false
      }
    },

    /**
     * Scroll to the section of the table with the latest data
     */
    scrollTableBottom() {
      const currentScroll = document.getElementById(this.scrollId).scrollTop;
      this.loading = false;

      const rowElement = document.getElementById("rowId");
      if (rowElement) {
        const rowHeight = rowElement.clientHeight;
        for (let i = 0; i < this.pagination; i++) {
          const offset = rowHeight * i;
          document.getElementById(this.scrollId).scrollTo({
            top: currentScroll - offset,
            behavior: "smooth",
          });
        }
      }
    },

    onScrollTop() {
      if (this.currentPageTop !== 1) {
        this.pageNumber--;
        this.getResultsTop();
      }
    },

    async getResultsTop() {
      this.loading = true;
      this.currentPageTop--;

      try {
        let queryParams = {
          pageNumber: this.currentPageTop,
          paginate: this.pagination,
        };

        if (this.sortOrder !== null) {
          queryParams["sort"] = this.sortAttributeWithPrefix;
        }

        let resultsObject = await this.SEARCH_OBJECTS_BY_ATTRIBUTE({
          layer: this.layer,
          filter: this.attribute,
          value: this.value,
          params: queryParams,
        });

        this.lastPage = resultsObject.data.meta.last_page;
        resultsObject = this.removeObjectLayerPrefixFromAttributeKeys(
          resultsObject.data.data[0].objects,
          resultsObject.data.data[0].prefix
        );

        const allResults = [...resultsObject, ...this.objects];
        let itemsOptions = this.processingResultsData(allResults);

        this.objects = Array.from(new Set(itemsOptions.map(a => a.id))).map(id => { return itemsOptions.find(a => a.id === id) })
        if (this.objects.length > (3 * this.pagination)) {
          let numberOfElementsToRemove = this.objects.length - (3 * this.pagination);
          this.objects.splice(this.objects.length - numberOfElementsToRemove, numberOfElementsToRemove);
          this.currentPageBot--;
        }

        this.scrollTableTop();
      } catch (error) {
        console.error(error);
        this.handleError(error);
        this.objects = [];
      } finally {
        this.loading = false;
      }
    },

    scrollTableTop() {
      const currentScroll = document.getElementById(this.scrollId).scrollTop;
      this.loading = false;

      const rowElement = document.getElementById("rowId");
      if (rowElement) {
        const rowHeight = rowElement.clientHeight;
        for (let i = 0; i < this.pagination; i++) {
          const offset = rowHeight * i;
          document.getElementById(this.scrollId).scrollTo({
            top: currentScroll + offset,
            behavior: "smooth",
          });
        }
      }
    },

    onSortClick(sortOrder, index) {
      if (sortOrder === "asc") {
        this.sortOrder = this.headers[index].varName;
      } else if (sortOrder === "desc") {
        this.sortOrder = `-${this.headers[index].varName}`;
      } else {
        this.sortOrder = sortOrder;
      }
      this.initialSearch();
    },

    ///////////////////////////////////////////////////////////
    /////////////////// UTIL METHODS //////////////////////////
    ///////////////////////////////////////////////////////////

    /**
     * Removes the prefix of the layer from the objects attribute keys
     * @param {Array} objects array of objects returned
     * @param {String} prefix the prefix of the object layer
     */
    removeObjectLayerPrefixFromAttributeKeys(objects, prefix) {
      let objectsCloneArray = [...objects];
      for (let i = 0; i < objectsCloneArray.length; i++) {
        let readyFraction = Object.fromEntries(
          Object.entries(
            objectsCloneArray[i],
          ).map(([k, v]) => [k.replace(prefix, ''), v]),
        )
        objectsCloneArray[i] = readyFraction
      }
      return objectsCloneArray;
    },

    /**
     * Handles errors from the search functions
     * @param {Error} error
     */
    handleError(error) {
      if (error.response) {
        if (error.response.status !== 404) {
          this.$toasted.global.toast_error(err)
        } else if (error.response.status === 404 && error.response.data === "No objects found") {
          this.objects = [];
        }
      }
    },

    /**
     * Retrieves the geoms from the objects and processes the new data
     *
     * @param {Array} allResults old results and the new combined
     */
    processingResultsData(allResults) {
      let itemsOptions = [];
      let itemsCoordinates = [];
      allResults.forEach((item) => {
        if (item.geom) {
          itemsOptions.push(item);
          itemsCoordinates.push(item.geom);
        } else {
          let geometryObject = item.node ? item.node : item.geometry;
          let geometryValue;
          for(const [key, value] of Object.entries(geometryObject)) {
            if (key.includes('geo_json')) {
              geometryValue = value;
            }
          }

          itemsOptions.push({
            label: item.label,
            id: item.id,
            type: this.layer,
            uuid: item.uuid,
            geom: geometryValue,
          })

          itemsCoordinates.push(geometryValue);
        }
      })

      bus.$emit('draw-features', itemsCoordinates)

      return itemsOptions;
    }
  }
}
</script>

<style scoped>
.results-card,
.no-results {
  height: 100%;
  max-height: 100%;
  background-color: #fff;
  position: relative;
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.25);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
}

.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.results-card > span,
.no-results > span {
  color: var(--app-primary-base);
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  line-height: normal;
}

.table-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overflow: auto;
}

.table-title {
  color: #767676;
  font-family: Lato;
  font-size: 10px;
  font-style: normal;
  font-weight: 800;
  line-height: 20px;
  text-transform: uppercase;
  margin-bottom: 16px;
}
</style>


// WEBPACK FOOTER //
// src/newComponents/WorkspacesManagement/GlobalSearch/GlobalSearchResults.vue